import { createSlice } from "@reduxjs/toolkit";
import {
  MoveFileAction,
  RenameFilesAction,
  UploadFilesAction,
} from "store/middlewares/files";
import {
  CreateShareLinkFileFolderAction,
  UpdateFolderAction,
} from "store/middlewares/folders";
import {
  GetHelpAndFeedbackAction,
  GetHomeFilesFolderAction,
  GetLockedFileFolderListAction,
  GetRecentFilesFolderAction,
  GetSettingAction,
  GetSharedFilesFolderAction,
  GetStarredFileFolderListAction,
  LockUnlockFileFolderAction,
  SearchFilesFolderAction,
  StarUnstarFileFolderAction,
  GetOpenFileAction,
  GetLanguageAction,
  PeopleSuggestionAction,
  GetAllReviewListAction,
  GetOpenFilePublicAction,
  GetOpenPublicFolderAction,
  GetStaticPagesAction,
} from "store/middlewares/general";
import {
  AddSearchHistoryAction,
  ClearSearchHistoryAction,
  GetSearchHistoryAction,
} from "store/middlewares/search";
import { matchesDateAndLocalFilter } from "utils/filterUtils";

const sortItems = (items = [], sortBy, sortOrder, useFileNameOnly = false) => {
  if (!sortBy || !sortOrder) return items;

  return [...items].sort((a, b) => {
    let comparison = 0;

    if (sortBy === "name") {
      const nameA = useFileNameOnly
        ? a?.file_name || ""
        : a?.name || a?.file_name || "";

      const nameB = useFileNameOnly
        ? b?.file_name || ""
        : b?.name || b?.file_name || "";

      comparison = nameA.localeCompare(nameB, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    } else {
      const dateA = Number(a?.created_at || 0);
      const dateB = Number(b?.created_at || 0);

      comparison = dateA - dateB;
    }

    return sortOrder === "desc" ? -comparison : comparison;
  });
};

const insertByLastOpenedAt = (list, entry, getComparable = (item) => item) => {
  if (!Array.isArray(list) || !entry) return;

  const comparable = getComparable(entry);
  if (!comparable?.id) return;

  const alreadyExists = list.some((item) => {
    const c = getComparable(item);
    return c?.id === comparable.id;
  });
  if (alreadyExists) return;

  const newTime = Number(comparable.last_opened_at || 0);

  let insertIndex = list.length;
  for (let i = 0; i < list.length; i++) {
    const existingTime = Number(getComparable(list[i])?.last_opened_at || 0);
    if (newTime > existingTime) {
      insertIndex = i;
      break;
    }
  }

  list.splice(insertIndex, 0, entry);
};

export const generalSlice = createSlice({
  name: "general",
  initialState: {
    staticPagesLoading: false,
    staticPages: null,
    language: null,
    languageLoading: false,
    starredList: null,
    starredListLoading: false,
    lockedFileFolder: null,
    lockedFileFolderLoading: false,
    homeFilesFolder: null,
    homeFilesFolderLoading: false,
    recentFilesFolder: null,
    recentFilesFolderLoading: false,
    sharedFilesFolder: null,
    sharedFilesFolderLoading: false,
    searchFilesFolder: null,
    searchFilesFolderLoading: false,
    helpAndFeedbackLoading: false,
    helpAndFeedback: null,
    uploadFileLoading: false,
    uploadFileError: false,
    settingLoading: false,
    setting: null,
    uploadNewFile: { list: [], display: false },
    uploadFolderState: { name: "", status: null, display: false },
    filterValues: {},
    openFile: null,
    openFileLoading: false,
    peopleSuggestion: null,
    peopleSuggestionLoading: false,
    reviewListLoading: false,
    reviewList: [],
    publicFile: null,
    publicFileLoading: false,
    publicFolder: null,
    publicFolderLoading: false,
    searchHistory: null,
    searchHistoryLoading: false,
    sortOrder: {
      sortBy: "name",
      sortOrder: "asc",
    },
  },
  reducers: {
    removeUploadFile: (state, action) => {
      const tempId = action.payload;
      state.uploadNewFile.list = state.uploadNewFile.list.filter(
        (f) => f.tempId !== tempId,
      );
      if (state.uploadNewFile.list.length === 0) {
        state.uploadNewFile.display = false;
        state.uploadFileLoading = false;
      }
    },
    updateSortOrderInGeneral: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.sortOrder = { sortBy, sortOrder };
    },
    uploadFileHandle: (state, action) => {
      const newFiles = action.payload.files.map((file) => ({
        tempId: file.tempId,
        name: file.name,
        status: "uploading",
        fileRef: file.fileRef || null,
        folderId: file.folderId ?? null,
        isReplaced: file.isReplaced || false,
        oldFileId: file.oldFileId || null,
      }));

      state.uploadNewFile = {
        ...state.uploadNewFile,
        display: true,
        list: [...state.uploadNewFile.list, ...newFiles],
      };
    },
    cancelUploadingFile: (state, action) => {
      const tempId = action.payload;
      const file = state.uploadNewFile.list.find((f) => f.tempId === tempId);
      if (file) {
        file.status = "canceled";
      }
    },
    cancelUploadingAllFiles: (state) => {
      state.uploadNewFile.list.forEach((file) => {
        if (file.status === "uploading") {
          file.status = "canceled";
        }
      });
    },
    setFilterValues: (state, action) => {
      state.filterValues = action.payload;
    },
    resetFilterValues: (state) => {
      state.filterValues = {};
    },
    updateFileProgress: (state, action) => {
      const { tempId, progress } = action.payload;
      const file = state.uploadNewFile.list?.find((f) => f.tempId === tempId);
      if (file) {
        file.progress = progress;
      }
    },
    clearUploadFiles: (state) => {
      state.uploadNewFile = {
        list: [],
        display: false,
      };
      state.uploadFileLoading = false;
    },
    uploadFolderHandle: (state, action) => {
      state.uploadFolderState = {
        name: action.payload.name,
        status: "uploading",
        display: true,
        folderData: action.payload.folderData || null,
      };
    },
    setFolderUploadStatus: (state, action) => {
      state.uploadFolderState.status = action.payload;
    },
    clearFolderUpload: (state) => {
      state.uploadFolderState = { name: "", status: null, display: false };
    },

    renameFileInHome: (state, action) => {
      const updatedFile = action.payload;

      const directFileLists = [
        state.homeFilesFolder?.my_files?.data,
        state.searchFilesFolder?.files,
        state.sharedFilesFolder?.files,
        state.lockedFileFolder?.files,
        state.starredList?.data?.files,
        state.recentFilesFolder?.data,
      ];

      directFileLists.filter(Boolean).forEach((list) => {
        const index = list.findIndex((f) => f?.id === updatedFile?.id);
        if (index !== -1) {
          list[index] = updatedFile;
        }
      });

      if (state.openFile?.id === updatedFile?.id) {
        state.openFile = updatedFile;
      }

      const nestedFileLists = [
        state.homeFilesFolder?.shared_with_me?.data,
        state.homeFilesFolder?.favourite_items?.data,
      ];

      nestedFileLists.filter(Boolean).forEach((list) => {
        list.forEach((item) => {
          if (item?.file?.id === updatedFile?.id) {
            item.file = updatedFile;
          }
        });
      });

      if (state.starredList?.data?.files) {
        const { sortBy, sortOrder } = state.sortOrder || {};
        state.starredList.data.files = sortItems(
          state.starredList.data.files,
          sortBy,
          sortOrder,
          true,
        );
      }
    },

    // ========== UPDATED: updateFolderItemsInHome ==========
    updateFolderItemsInHome: (state, action) => {
      const { id } = action.payload;

      const updateFolder = (folder) => {
        if (folder?.id === id) {
          folder.name = action.payload.name;
          folder.full_path = action.payload.full_path;
          folder.folder_size = action.payload.folder_size;
          folder.file_size = action.payload.file_size;
        }
      };

      const directFolderLists = [
        state.homeFilesFolder?.my_folders?.data,
        state.searchFilesFolder?.folders,
        state.sharedFilesFolder?.folders,
        state.lockedFileFolder?.folders,
        state.starredList?.data?.folders,
        state.recentFilesFolder?.data,
      ];

      directFolderLists.forEach((list) => {
        list?.forEach(updateFolder);
      });

      const nestedFolderLists = [
        state.homeFilesFolder?.shared_with_me?.data,
        state.homeFilesFolder?.favourite_items?.data,
      ];

      nestedFolderLists.forEach((list) => {
        list?.forEach((item) => {
          if (item?.folder?.id === id) {
            item.folder.name = action.payload.name;
            item.folder.full_path = action.payload.full_path;
            item.folder.folder_size = action.payload.folder_size;
            item.folder.file_size = action.payload.file_size;
          }
        });
      });

      // Re-sort starred folders with the CURRENT sort order
      if (state.starredList?.data?.folders) {
        const { sortBy, sortOrder } = state.sortOrder || {};
        state.starredList.data.folders = sortItems(
          state.starredList.data.folders,
          sortBy,
          sortOrder,
        );
      }
    },
    moveFilesAndFoldersGeneral: (state, action) => {
      const response = action.payload || {};

      const movedFolders = response.folders || [];
      const movedFiles = response.files || [];
      const newParentId = response.new_parent;

      // --------------------------------------------------
      // 1. Update parent_id / folder_id / full_path etc. on existing items
      // --------------------------------------------------
      const fileLists = [
        state.homeFilesFolder?.my_files?.data,
        state.searchFilesFolder?.files,
        state.sharedFilesFolder?.files,
        state.lockedFileFolder?.files,
        state.starredList?.data?.files,
        state.recentFilesFolder?.data,
        state.openFile ? [state.openFile] : null,
      ];

      fileLists.forEach((list) => {
        list?.forEach((file) => {
          const movedFile = movedFiles.find((item) => item.id === file.id);

          if (movedFile) {
            file.folder_id = movedFile.folder_id ?? newParentId;
            if (movedFile.path_info !== undefined) {
              file.path_info = movedFile.path_info;
            }
            if (movedFile.name !== undefined) {
              file.name = movedFile.name;
            }
            if (movedFile.file_name !== undefined) {
              file.file_name = movedFile.file_name;
            }
          }
        });
      });

      const folderLists = [
        state.homeFilesFolder?.my_folders?.data,
        state.searchFilesFolder?.folders,
        state.sharedFilesFolder?.folders,
        state.lockedFileFolder?.folders,
        state.starredList?.data?.folders,
        state.recentFilesFolder?.data,
      ];

      folderLists.forEach((list) => {
        list?.forEach((folder) => {
          const movedFolder = movedFolders.find(
            (item) => item.id === folder.id,
          );

          if (movedFolder) {
            folder.parent_id = movedFolder.parent_id ?? newParentId;
            if (movedFolder.full_path !== undefined) {
              folder.full_path = movedFolder.full_path;
            }
            if (movedFolder.name !== undefined) {
              folder.name = movedFolder.name;
            }
          }
        });
      });

      // Update nested file/folder objects in Home (shared_with_me, favourite_items)
      const nestedLists = [
        state.homeFilesFolder?.shared_with_me?.data,
        state.homeFilesFolder?.favourite_items?.data,
      ];

      nestedLists.forEach((list) => {
        list?.forEach((item) => {
          if (item?.file) {
            const movedFile = movedFiles.find(
              (file) => file.id === item.file.id,
            );

            if (movedFile) {
              item.file.folder_id = movedFile.folder_id ?? newParentId;
              if (movedFile.full_path !== undefined) {
                item.file.full_path = movedFile.full_path;
              }
              if (movedFile.name !== undefined) {
                item.file.name = movedFile.name;
              }
              if (movedFile.file_name !== undefined) {
                item.file.file_name = movedFile.file_name;
              }
            }
          }

          if (item?.folder) {
            const movedFolder = movedFolders.find(
              (folder) => folder.id === item.folder.id,
            );

            if (movedFolder) {
              item.folder.parent_id = movedFolder.parent_id ?? newParentId;
              if (movedFolder.full_path !== undefined) {
                item.folder.full_path = movedFolder.full_path;
              }
              if (movedFolder.name !== undefined) {
                item.folder.name = movedFolder.name;
              }
            }
          }
        });
      });

      // --------------------------------------------------
      // 2. Locked-list rules for secure folders & files
      // --------------------------------------------------
      if (!state.lockedFileFolder) return;

      if (!Array.isArray(state.lockedFileFolder.folders)) {
        state.lockedFileFolder.folders = [];
      }
      if (!Array.isArray(state.lockedFileFolder.files)) {
        state.lockedFileFolder.files = [];
      }

      const lockedFolders = state.lockedFileFolder.folders;
      const lockedFiles = state.lockedFileFolder.files;
      const lockedFolderIds = new Set(lockedFolders.map((f) => f.id));
      const lockedFileIds = new Set(lockedFiles.map((f) => f.id));

      // ----- Secure folders -----
      movedFolders.forEach((movedFolder) => {
        if (!movedFolder?.is_secure) return;

        const folderId = movedFolder.id;
        const parentId = movedFolder.parent_id ?? newParentId;

        const isInLockedList = lockedFolderIds.has(folderId);
        const parentIsInLockedList = parentId && lockedFolderIds.has(parentId);

        if (parentIsInLockedList) {
          // Parent already shown → remove the child folder
          if (isInLockedList) {
            state.lockedFileFolder.folders = lockedFolders.filter(
              (f) => f.id !== folderId,
            );
          }
        } else {
          // Parent not in locked list → make sure this folder is shown
          if (!isInLockedList) {
            if (
              !state.lockedFileFolder.activeFilter ||
              matchesDateAndLocalFilter(
                movedFolder,
                state.lockedFileFolder.activeFilter,
              )
            ) {
              state.lockedFileFolder.folders.unshift({ ...movedFolder });
            }
          }
        }
      });

      // ----- Secure files -----
      movedFiles.forEach((movedFile) => {
        if (!movedFile?.is_secure) return;

        const fileId = movedFile.id;
        const parentId = movedFile.folder_id ?? newParentId;

        const isInLockedList = lockedFileIds.has(fileId);
        const parentIsInLockedList = parentId && lockedFolderIds.has(parentId);

        if (parentIsInLockedList) {
          // Parent folder is already shown → remove the file
          if (isInLockedList) {
            state.lockedFileFolder.files = lockedFiles.filter(
              (f) => f.id !== fileId,
            );
          }
        } else {
          // Parent folder is NOT in locked list → make sure the file is shown
          if (!isInLockedList) {
            if (
              !state.lockedFileFolder.activeFilter ||
              matchesDateAndLocalFilter(
                movedFile,
                state.lockedFileFolder.activeFilter,
              )
            ) {
              state.lockedFileFolder.files.unshift({ ...movedFile });
            }
          }
        }
      });
    },
    uploadFileInGeneral: (state, action) => {
      const response = action.payload;
      if (response && response.uploaded) {
        if (state.recentFilesFolder?.data) {
          const validFiles = response.uploaded.filter((file) =>
            matchesDateAndLocalFilter(
              file,
              state.recentFilesFolder?.activeFilter,
            ),
          );
          state.recentFilesFolder.data = [
            ...validFiles,
            ...state.recentFilesFolder.data,
          ];
        }
      }
    },
    trashFilesAndFoldersGeneral: (state, action) => {
      const trashedFolders = action.payload?.folders || [];
      const trashedFiles = action.payload?.files || [];

      const trashedFolderIds = trashedFolders.map((f) => f.id);
      const trashedFileIds = trashedFiles.map((f) => f.id);

      const allTrashedIds = [...trashedFolderIds, ...trashedFileIds];

      if (allTrashedIds.length === 0) return;

      const removeByIds = (list, ids) =>
        list?.filter((item) => !ids.includes(item.id));

      // Update recentFilesFolder
      if (state.recentFilesFolder?.data) {
        const updatedData = removeByIds(
          state.recentFilesFolder.data,
          allTrashedIds,
        );
        state.recentFilesFolder.data = updatedData;
        state.recentFilesFolder.total = updatedData.length;
      }

      // Update homeFilesFolder
      if (state.homeFilesFolder?.my_folders?.data) {
        state.homeFilesFolder.my_folders.data = removeByIds(
          state.homeFilesFolder.my_folders.data,
          allTrashedIds,
        );
      }

      if (state.homeFilesFolder?.my_files?.data) {
        state.homeFilesFolder.my_files.data = removeByIds(
          state.homeFilesFolder.my_files.data,
          allTrashedIds,
        );
      }

      // Update searchFilesFolder
      if (state.searchFilesFolder?.folders) {
        state.searchFilesFolder.folders = removeByIds(
          state.searchFilesFolder.folders,
          allTrashedIds,
        );
      }

      if (state.searchFilesFolder?.files) {
        state.searchFilesFolder.files = removeByIds(
          state.searchFilesFolder.files,
          allTrashedIds,
        );
      }

      if (state.lockedFileFolder?.folders) {
        state.lockedFileFolder.folders = removeByIds(
          state.lockedFileFolder.folders,
          allTrashedIds,
        );
      }

      if (state.lockedFileFolder?.files) {
        state.lockedFileFolder.files = removeByIds(
          state.lockedFileFolder.files,
          allTrashedIds,
        );
      }

      if (state.lockedFileFolder?.files) {
        state.lockedFileFolder.files = state.lockedFileFolder.files.filter(
          (file) =>
            !trashedFileIds.includes(file.id) &&
            !trashedFolderIds.includes(file.folder_id),
        );
      }

      if (state.starredList?.data?.folders) {
        state.starredList.data.folders = removeByIds(
          state.starredList.data.folders,
          allTrashedIds,
        );
      }

      if (state.starredList?.data?.files) {
        state.starredList.data.files = removeByIds(
          state.starredList.data.files,
          allTrashedIds,
        );
      }

      if (trashedFileIds.includes(state?.openFile?.id)) {
        state.openFile = { ...state.openFile, is_trashed: true };
      }

      // Remove from Home → Shared with me and manage has_more locally
      if (state.homeFilesFolder?.shared_with_me?.data) {
        const sharedData = state.homeFilesFolder.shared_with_me.data;
        const filteredShared = sharedData.filter(
          (item) =>
            !(
              (item?.file?.id && trashedFileIds.includes(item.file.id)) ||
              (item?.folder?.id && trashedFolderIds.includes(item.folder.id)) ||
              (item?.id && allTrashedIds.includes(item.id))
            ),
        );
        state.homeFilesFolder.shared_with_me.data = filteredShared;

        // has_more = true when more than 4 items remain
        state.homeFilesFolder.shared_with_me.has_more =
          filteredShared.length > 4;
      }

      // Also remove from sharedFilesFolder lists if present
      if (state.sharedFilesFolder?.files) {
        state.sharedFilesFolder.files = removeByIds(
          state.sharedFilesFolder.files,
          allTrashedIds,
        );
      }
      if (state.sharedFilesFolder?.folders) {
        state.sharedFilesFolder.folders = removeByIds(
          state.sharedFilesFolder.folders,
          allTrashedIds,
        );
      }
      if (state.homeFilesFolder?.favourite_items?.data) {
        const favData = state.homeFilesFolder.favourite_items.data;
        const filteredFav = favData.filter(
          (item) =>
            !(
              (item?.file?.id && trashedFileIds.includes(item.file.id)) ||
              (item?.folder?.id && trashedFolderIds.includes(item.folder.id)) ||
              (item?.id && allTrashedIds.includes(item.id))
            ),
        );
        state.homeFilesFolder.favourite_items.data = filteredFav;

        state.homeFilesFolder.favourite_items.has_more = filteredFav.length > 4;
      }
    },
    starUnstarItemsGeneral: (state, action) => {
      const response = action.payload || {};

      const files = response?.files || [];
      const folders = response?.folders || [];
      const isStarred = response?.action === "starred";

      // Update files everywhere
      const fileLists = [
        state.homeFilesFolder?.my_files?.data,
        state.searchFilesFolder?.files,
        state.sharedFilesFolder?.files,
        state.lockedFileFolder?.files,
        state.starredList?.data?.files,
        state.recentFilesFolder?.data,
        state.openFile ? [state.openFile] : null,
      ];

      fileLists.forEach((list) => {
        list?.forEach((file) => {
          const updatedFile = files.find((item) => item.id === file.id);

          if (updatedFile) {
            file.is_starred = updatedFile.is_starred;
          }
        });
      });

      // Update folders everywhere
      const folderLists = [
        state.homeFilesFolder?.my_folders?.data,
        state.searchFilesFolder?.folders,
        state.sharedFilesFolder?.folders,
        state.lockedFileFolder?.folders,
        state.starredList?.data?.folders,
        state.recentFilesFolder?.data,
      ];

      folderLists.forEach((list) => {
        list?.forEach((folder) => {
          const updatedFolder = folders.find((item) => item.id === folder.id);

          if (updatedFolder) {
            folder.is_starred = updatedFolder.is_starred;
          }
        });
      });

      // Update nested file/folder objects
      const nestedLists = [
        state.homeFilesFolder?.shared_with_me?.data,
        state.homeFilesFolder?.favourite_items?.data,
      ];

      nestedLists.forEach((list) => {
        list?.forEach((item) => {
          if (item?.file) {
            const updatedFile = files.find((file) => file.id === item.file.id);

            if (updatedFile) {
              item.file.is_starred = updatedFile.is_starred;
            }
          }

          if (item?.folder) {
            const updatedFolder = folders.find(
              (folder) => folder.id === item.folder.id,
            );

            if (updatedFolder) {
              item.folder.is_starred = updatedFolder.is_starred;
            }
          }
        });
      });

      // Ensure starred list exists
      if (!state.starredList) {
        state.starredList = {
          data: {
            files: [],
            folders: [],
          },
        };
      }

      if (!state.starredList.data) {
        state.starredList.data = {
          files: [],
          folders: [],
        };
      }

      if (!state.starredList.data.files) {
        state.starredList.data.files = [];
      }

      if (!state.starredList.data.folders) {
        state.starredList.data.folders = [];
      }

      if (!state.homeFilesFolder?.favourite_items) {
        state.homeFilesFolder = {
          ...state.homeFilesFolder,
          favourite_items: {},
        };
      }
      // Ensure favourite items exists
      if (!state.homeFilesFolder?.favourite_items) {
        state.homeFilesFolder.favourite_items = {
          data: [],
        };
      }

      if (!state.homeFilesFolder.favourite_items.data) {
        state.homeFilesFolder.favourite_items.data = [];
      }

      const favouriteItems = state.homeFilesFolder.favourite_items.data;

      if (isStarred) {
        // Add starred files to starred list
        files.forEach((file) => {
          if (file.is_starred) {
            if (
              matchesDateAndLocalFilter(file, state.starredList?.activeFilter)
            ) {
              const alreadyExists = state.starredList.data.files.some(
                (item) => item.id === file.id,
              );

              if (!alreadyExists) {
                state.starredList.data.files.unshift({
                  ...file,
                  is_starred: true,
                });
              }
            }

            // Add starred file to favourite items
            const favouriteExists = favouriteItems.some(
              (item) => item?.file?.id === file.id,
            );

            if (!favouriteExists) {
              favouriteItems.unshift({
                type: "file",
                id: file?.id,
                file: {
                  ...file,
                  is_starred: true,
                },
              });
            }
          }
        });

        // Add starred folders to starred list
        folders.forEach((folder) => {
          if (folder.is_starred) {
            if (
              matchesDateAndLocalFilter(folder, state.starredList?.activeFilter)
            ) {
              const alreadyExists = state.starredList.data.folders.some(
                (item) => item.id === folder.id,
              );

              if (!alreadyExists) {
                state.starredList.data.folders.unshift({
                  ...folder,
                  is_starred: true,
                });
              }
            }

            // Add starred folder to favourite items
            const favouriteExists = favouriteItems.some(
              (item) => item?.folder?.id === folder.id,
            );

            if (!favouriteExists) {
              favouriteItems.unshift({
                type: "folder",
                id: folder?.id,
                folder: {
                  ...folder,
                  is_starred: true,
                },
              });
            }
          }
        });

        const { sortBy, sortOrder } = state.sortOrder || {};

        if (Array.isArray(state.starredList.data.files)) {
          state.starredList.data.files = sortItems(
            state.starredList.data.files,
            sortBy,
            sortOrder,
            true,
          );
        }

        if (Array.isArray(state.starredList.data.folders)) {
          state.starredList.data.folders = sortItems(
            state.starredList.data.folders,
            sortBy,
            sortOrder,
          );
        }
      } else {
        // Remove unstarred files from starred list
        const fileIds = files.map((file) => file.id);

        state.starredList.data.files = state.starredList.data.files.filter(
          (file) => !fileIds.includes(file.id),
        );

        // Remove unstarred folders from starred list
        const folderIds = folders.map((folder) => folder.id);

        state.starredList.data.folders = state.starredList.data.folders.filter(
          (folder) => !folderIds.includes(folder.id),
        );

        // Remove unstarred files/folders from favourite items
        state.homeFilesFolder.favourite_items.data = favouriteItems.filter(
          (item) => {
            if (item?.file) {
              return !fileIds.includes(item.file.id);
            }

            if (item?.folder) {
              return !folderIds.includes(item.folder.id);
            }

            return true;
          },
        );
      }
      if (state.homeFilesFolder?.favourite_items?.data) {
        state.homeFilesFolder.favourite_items.has_more =
          state.homeFilesFolder.favourite_items.data.length > 4;
      }
    },

    lockUnlockItemsGeneral: (state, action) => {
      const response = action.payload || {};

      const files = response?.files || [];
      const folders = response?.folders || [];

      const isSecure = response?.action === "secured";

      // Update files everywhere
      const fileLists = [
        state.homeFilesFolder?.my_files?.data,
        state.searchFilesFolder?.files,
        state.sharedFilesFolder?.files,
        state.lockedFileFolder?.files,
        state.starredList?.data?.files,
        state.recentFilesFolder?.data,
        state.openFile ? [state.openFile] : null,
      ];

      fileLists.forEach((list) => {
        list?.forEach((file) => {
          const updatedFile = files.find((item) => item.id === file.id);

          if (updatedFile) {
            file.is_secure = updatedFile.is_secure;
            file.secure_type = updatedFile.secure_type;
            file.secure_hash = updatedFile.secure_hash;
          }
        });
      });

      // Update folders everywhere
      const folderLists = [
        state.homeFilesFolder?.my_folders?.data,
        state.searchFilesFolder?.folders,
        state.sharedFilesFolder?.folders,
        state.lockedFileFolder?.folders,
        state.starredList?.data?.folders,
        state.recentFilesFolder?.data,
      ];

      folderLists.forEach((list) => {
        list?.forEach((folder) => {
          const updatedFolder = folders.find((item) => item.id === folder.id);

          if (updatedFolder) {
            folder.is_secure = updatedFolder.is_secure;
            folder.secure_type = updatedFolder.secure_type;
            folder.secure_hash = updatedFolder.secure_hash;
            folder.drive_secure_item = updatedFolder.drive_secure_item;
          }
        });
      });

      // Update nested file/folder objects
      const nestedLists = [
        state.homeFilesFolder?.shared_with_me?.data,
        state.homeFilesFolder?.favourite_items?.data,
      ];

      nestedLists.forEach((list) => {
        list?.forEach((item) => {
          if (item?.file) {
            const updatedFile = files.find((file) => file.id === item.file.id);

            if (updatedFile) {
              item.file.is_secure = updatedFile.is_secure;
              item.file.secure_type = updatedFile.secure_type;
              item.file.secure_hash = updatedFile.secure_hash;
            }
          }

          if (item?.folder) {
            const updatedFolder = folders.find(
              (folder) => folder.id === item.folder.id,
            );

            if (updatedFolder) {
              item.folder.is_secure = updatedFolder.is_secure;
              item.folder.secure_type = updatedFolder.secure_type;
              item.folder.secure_hash = updatedFolder.secure_hash;
              item.folder.drive_secure_item = updatedFolder.drive_secure_item;
            }
          }
        });
      });

      // Secure item
      if (isSecure) {
        if (!state.lockedFileFolder) {
          state.lockedFileFolder = {
            folders: [],
            files: [],
          };
        }

        if (!state.lockedFileFolder.folders) {
          state.lockedFileFolder.folders = [];
        }

        if (!state.lockedFileFolder.files) {
          state.lockedFileFolder.files = [];
        }

        // Add secure folders
        folders.forEach((folder) => {
          if (folder.is_secure) {
            if (
              matchesDateAndLocalFilter(
                folder,
                state.lockedFileFolder?.activeFilter,
              )
            ) {
              const alreadyExists = state.lockedFileFolder.folders.some(
                (item) => item.id === folder.id,
              );

              if (!alreadyExists) {
                state.lockedFileFolder.folders.unshift({
                  ...folder,
                });
              }
            }
          }
        });

        // Add secure files
        files.forEach((file) => {
          if (file.is_secure) {
            if (
              matchesDateAndLocalFilter(
                file,
                state.lockedFileFolder?.activeFilter,
              )
            ) {
              const alreadyExists = state.lockedFileFolder.files.some(
                (item) => item.id === file.id,
              );

              if (!alreadyExists) {
                state.lockedFileFolder.files.unshift({
                  ...file,
                });
              }
            }
          }
        });
      } else {
        // Unlock - remove from locked list
        const fileIds = files.map((file) => file.id);
        const folderIds = folders.map((folder) => folder.id);

        if (state.lockedFileFolder?.files) {
          state.lockedFileFolder.files = state.lockedFileFolder.files.filter(
            (file) => !fileIds.includes(file.id),
          );
        }

        if (state.lockedFileFolder?.folders) {
          state.lockedFileFolder.folders =
            state.lockedFileFolder.folders.filter(
              (folder) => !folderIds.includes(folder.id),
            );
        }
      }
    },
    updateFileAndFolderGeneral: (state, action) => {
      const response = action.payload || {};

      const { profileId, user_id, folder_id, file_id, permission } = response;

      console.log("update_in_general", { profileId, user_id });

      // Only update the current user's permission
      if (!profileId || !user_id || user_id !== profileId) {
        return;
      }

      const targetId = folder_id || file_id;

      console.log("update_in_general data", response);

      if (!targetId) {
        return;
      }

      // Update normal file/folder lists
      const lists = [
        state.homeFilesFolder?.my_files?.data,
        state.homeFilesFolder?.my_folders?.data,
        state.starredList?.data?.files,
        state.starredList?.data?.folders,
        state.sharedFilesFolder?.files,
        state.sharedFilesFolder?.folders,
        state.recentFilesFolder?.data,
        state.searchFilesFolder?.files,
        state.searchFilesFolder?.folders,
        state.openFile ? [state.openFile] : null,
      ];

      lists.forEach((list) => {
        list?.forEach((item) => {
          if (item?.id === targetId) {
            item.permission = permission;
          }
        });
      });

      // Update nested shared/favourite items
      const nestedLists = [
        state.homeFilesFolder?.shared_with_me?.data,
        state.homeFilesFolder?.favourite_items?.data,
      ];

      nestedLists.forEach((list) => {
        list?.forEach((item) => {
          if (folder_id && item?.folder?.id === folder_id) {
            item.folder.permission = permission;
          }

          if (file_id && item?.file?.id === file_id) {
            item.file.permission = permission;
          }
        });
      });
    },
    removeFileAndFolderPemissionGeneral: (state, action) => {
      const response = action.payload || {};

      const { profileId, user_id, folder_id, file_id } = response;

      // Only remove the item if permission was removed
      // for the currently logged-in user.
      if (!profileId || !user_id || user_id !== profileId) {
        return;
      }

      // Remove from direct file/folder lists
      const directLists = [
        state.homeFilesFolder?.my_files?.data,
        state.homeFilesFolder?.my_folders?.data,
        state.starredList?.data?.files,
        state.starredList?.data?.folders,
        state.sharedFilesFolder?.files,
        state.sharedFilesFolder?.folders,
        state.lockedFileFolder?.files,
        state.lockedFileFolder?.folders,
        state.recentFilesFolder?.data,
        state.searchFilesFolder?.files,
        state.searchFilesFolder?.folders,
      ];

      directLists.forEach((list) => {
        if (!list) return;

        const filteredList = list.filter(
          (item) =>
            !(
              (file_id && item?.id === file_id) ||
              (folder_id && item?.id === folder_id)
            ),
        );

        list.length = 0;
        list.push(...filteredList);
      });

      // Remove from nested shared/favourite lists
      const nestedLists = [
        state.homeFilesFolder?.shared_with_me?.data,
        state.homeFilesFolder?.favourite_items?.data,
      ];

      nestedLists.forEach((list) => {
        if (!list) return;

        const filteredList = list.filter(
          (item) =>
            !(
              (file_id && item?.file?.id === file_id) ||
              (folder_id && item?.folder?.id === folder_id)
            ),
        );

        list.length = 0;
        list.push(...filteredList);
      });

      // Remove opened file if its permission was removed
      if (file_id && state.openFile?.id === file_id) {
        state.openFile = null;
      }
    },
    createFileAndFolderPermission: (state, action) => {
      const response = action.payload || {};

      const { profileId, file, folder, permissions = [], owner } = response;

      if (!profileId) return;

      // ------------------------------------------------------------------
      // 1. Always update is_shared on existing items in starred / locked
      //    (and related lists) when the backend provides the flag.
      // ------------------------------------------------------------------
      const isShared = file?.is_shared ?? folder?.is_shared;

      if (isShared !== undefined) {
        const fileId = file?.id || null;
        const folderId = folder?.id || null;

        if (fileId || folderId) {
          const updateIsShared = (list = [], isNested = false) => {
            if (!Array.isArray(list)) return;
            list.forEach((item) => {
              const target = isNested ? item?.file || item?.folder : item;
              if (!target) return;

              if (
                (fileId && target.id === fileId) ||
                (folderId && target.id === folderId)
              ) {
                target.is_shared = isShared;
              }
            });
          };

          // Starred page
          updateIsShared(state.starredList?.data?.files);
          updateIsShared(state.starredList?.data?.folders);

          // Locked page
          updateIsShared(state.lockedFileFolder?.files);
          updateIsShared(state.lockedFileFolder?.folders);

          // Nested favourite items (home) – mirrors starred
          updateIsShared(state.homeFilesFolder?.favourite_items?.data, true);

          // Optional consistency: also update home / recent / search / shared lists
          updateIsShared(state.homeFilesFolder?.my_files?.data);
          updateIsShared(state.homeFilesFolder?.my_folders?.data);
          updateIsShared(state.recentFilesFolder?.data);
          updateIsShared(state.searchFilesFolder?.files);
          updateIsShared(state.searchFilesFolder?.folders);
          updateIsShared(state.sharedFilesFolder?.files);
          updateIsShared(state.sharedFilesFolder?.folders);
          updateIsShared(state.homeFilesFolder?.shared_with_me?.data, true);

          if (fileId && state.openFile?.id === fileId) {
            state.openFile.is_shared = isShared;
          }
        }
      }

      // ------------------------------------------------------------------
      // 2. Add to Shared With Me / shared lists only when the permission
      //    was created for the currently logged-in user.
      // ------------------------------------------------------------------
      const currentPermission = permissions.find(
        (permission) => permission?.user?.id === profileId,
      );

      // Permission was not created for the current user
      if (!currentPermission) return;

      const permission = currentPermission.permission;

      // --------------------------------------------------
      // FILE
      // --------------------------------------------------
      if (file?.id) {
        const newFile = {
          ...file,
          permission,
          owner,
          is_shared: isShared ?? file.is_shared ?? true,
        };

        // Add to Shared With Me
        const sharedWithMe = state.homeFilesFolder?.shared_with_me?.data;

        if (Array.isArray(sharedWithMe)) {
          const alreadyExists = sharedWithMe.some(
            (item) => item?.file?.id === file.id,
          );

          if (!alreadyExists) {
            sharedWithMe.unshift({
              type: "file",
              file: newFile,
            });
          }
        }

        // Add to shared files listing
        const sharedFiles = state.sharedFilesFolder?.files;

        if (Array.isArray(sharedFiles)) {
          const alreadyExists = sharedFiles.some(
            (item) => item?.id === file.id,
          );

          if (
            !alreadyExists &&
            matchesDateAndLocalFilter(
              newFile,
              state.sharedFilesFolder?.activeFilter,
            )
          ) {
            sharedFiles.unshift(newFile);
          }
        }
      }

      // --------------------------------------------------
      // FOLDER
      // --------------------------------------------------
      if (folder?.id) {
        const newFolder = {
          ...folder,
          permission,
          owner,
          is_shared: isShared ?? folder.is_shared ?? true,
        };

        // Add to Shared With Me
        const sharedWithMe = state.homeFilesFolder?.shared_with_me?.data;

        if (Array.isArray(sharedWithMe)) {
          const alreadyExists = sharedWithMe.some(
            (item) => item?.folder?.id === folder.id,
          );

          if (!alreadyExists) {
            sharedWithMe.unshift({
              type: "folder",
              folder: newFolder,
            });
          }
        }

        // Add to shared folders listing
        const sharedFolders = state.sharedFilesFolder?.folders;

        if (Array.isArray(sharedFolders)) {
          const alreadyExists = sharedFolders.some(
            (item) => item?.id === folder.id,
          );

          if (
            !alreadyExists &&
            matchesDateAndLocalFilter(
              newFolder,
              state.sharedFilesFolder?.activeFilter,
            )
          ) {
            sharedFolders.unshift(newFolder);
          }
        }
      }
    },
    updateIsSharedGeneral: (state, action) => {
      const response = action.payload || {};

      // Prefer the explicit flag from the backend
      const isShared =
        response.is_shared ??
        response.file?.is_shared ??
        response.folder?.is_shared;

      if (isShared === undefined) return;

      const fileId =
        response.file?.id || response.file_id || response.fileId || null;
      const folderId =
        response.folder?.id || response.folder_id || response.folderId || null;

      if (!fileId && !folderId) return;

      const updateIsShared = (list = [], isNested = false) => {
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
          const target = isNested ? item?.file || item?.folder : item;
          if (!target) return;

          if (
            (fileId && target.id === fileId) ||
            (folderId && target.id === folderId)
          ) {
            target.is_shared = isShared;
          }
        });
      };

      // Direct lists
      updateIsShared(state.homeFilesFolder?.my_files?.data);
      updateIsShared(state.homeFilesFolder?.my_folders?.data);
      updateIsShared(state.starredList?.data?.files);
      updateIsShared(state.starredList?.data?.folders);
      updateIsShared(state.sharedFilesFolder?.files);
      updateIsShared(state.sharedFilesFolder?.folders);
      updateIsShared(state.lockedFileFolder?.files);
      updateIsShared(state.lockedFileFolder?.folders);
      updateIsShared(state.recentFilesFolder?.data);
      updateIsShared(state.searchFilesFolder?.files);
      updateIsShared(state.searchFilesFolder?.folders);

      // Nested lists
      updateIsShared(state.homeFilesFolder?.shared_with_me?.data, true);
      updateIsShared(state.homeFilesFolder?.favourite_items?.data, true);

      // Open file
      if (fileId && state.openFile?.id === fileId) {
        state.openFile.is_shared = isShared;
      }
    },
    recentItem: (state, action) => {
      const item = action.payload;
      if (!item?.id) return;
      if (
        !matchesDateAndLocalFilter(item, state.recentFilesFolder?.activeFilter)
      )
        return;

      // Ensure the recent structure exists
      if (!state.recentFilesFolder) {
        state.recentFilesFolder = {
          data: [],
          total: 0,
        };
      }
      if (!Array.isArray(state.recentFilesFolder.data)) {
        state.recentFilesFolder.data = [];
      }

      const list = state.recentFilesFolder.data;
      const existingIndex = list.findIndex((entry) => entry?.id === item.id);

      if (existingIndex === 0) {
        list[0] = item;
      } else if (existingIndex > 0) {
        list.splice(existingIndex, 1);
        list.unshift(item);
      } else {
        list.unshift(item);
        state.recentFilesFolder.total =
          (state.recentFilesFolder.total || 0) + 1;
      }

      if (!state.homeFilesFolder) {
        state.homeFilesFolder = {
          my_files: { data: [], has_more: false },
        };
      }
      if (!state.homeFilesFolder.my_files) {
        state.homeFilesFolder.my_files = { data: [], has_more: false };
      }
      if (!Array.isArray(state.homeFilesFolder.my_files.data)) {
        state.homeFilesFolder.my_files.data = [];
      }

      const myFiles = state.homeFilesFolder.my_files.data;

      if (
        matchesDateAndLocalFilter(item, state.homeFilesFolder?.activeFilter)
      ) {
        const homeFileIndex = myFiles.findIndex((f) => f?.id === item.id);

        if (homeFileIndex === 0) {
          myFiles[0] = item;
        } else if (homeFileIndex > 0) {
          myFiles.splice(homeFileIndex, 1);
          myFiles.unshift(item);
        } else {
          myFiles.unshift(item);
        }

        state.homeFilesFolder.my_files.has_more = myFiles.length > 4;
      }
    },
    recentFolder: (state, action) => {
      const folder = action.payload?.folder;
      if (!folder?.id) return;

      // Ensure the home structure exists
      if (!state.homeFilesFolder) {
        state.homeFilesFolder = {
          my_folders: { data: [], has_more: false },
        };
      }
      if (!state.homeFilesFolder.my_folders) {
        state.homeFilesFolder.my_folders = { data: [], has_more: false };
      }
      if (!Array.isArray(state.homeFilesFolder.my_folders.data)) {
        state.homeFilesFolder.my_folders.data = [];
      }

      const myFolders = state.homeFilesFolder.my_folders.data;
      const existingIndex = myFolders.findIndex((f) => f?.id === folder.id);

      if (existingIndex === 0) {
        // Already at the front – just refresh the data
        myFolders[0] = folder;
      } else if (existingIndex > 0) {
        // Remove from current position and put at the front
        myFolders.splice(existingIndex, 1);
        myFolders.unshift(folder);
      } else if (
        matchesDateAndLocalFilter(folder, state.homeFilesFolder?.activeFilter)
      ) {
        // Not present – add at the front (only if it matches the current filter)
        myFolders.unshift(folder);
      }

      // has_more = true when more than 4 items
      state.homeFilesFolder.my_folders.has_more = myFolders.length > 4;
    },
    acceptShareRequest: (state, action) => {
      const response = action.payload || {};

      const profileId = response.profileId;
      const requesterId = response.requester_id;

      if (!profileId || !requesterId || profileId !== requesterId) {
        return;
      }

      const { file, folder, permission } = response;

      // ========== FILE ==========
      if (file?.id) {
        // Map accept response → same shape as GetOpenFile API
        const newFile = {
          ...file,
          permission: permission || response.permission || "view",
          owner: response.owner || file.owner || null,
          shared_by: response.owner || file.shared_by || null,
          user: response.owner
            ? {
                id: response.owner.id,
                first_name: response.owner.first_name,
                last_name: response.owner.last_name,
                email: response.owner.email,
              }
            : file.user || null,
          is_starred: file.is_starred ?? false,
          path_info: file.path_info || file.file_name,
          // keep existing URLs from the nested file object
          file_url: file.file_url,
          view_url: file.view_url,
          thumbnail_url: file.thumbnail_url,
        };

        // Set openFile so FilePreviewPage can render immediately
        state.openFile = newFile;
        state.openFileLoading = false;

        // --- existing shared_with_me / sharedFilesFolder logic ---
        if (!state.homeFilesFolder?.shared_with_me) {
          state.homeFilesFolder = {
            ...state.homeFilesFolder,
            shared_with_me: { data: [] },
          };
        }
        if (!Array.isArray(state.homeFilesFolder.shared_with_me.data)) {
          state.homeFilesFolder.shared_with_me.data = [];
        }

        const sharedWithMe = state.homeFilesFolder.shared_with_me.data;
        if (!sharedWithMe.some((item) => item?.file?.id === file.id)) {
          sharedWithMe.unshift({
            type: "file",
            id: file.id,
            file: newFile,
          });
        }

        if (!state.sharedFilesFolder) {
          state.sharedFilesFolder = { files: [], folders: [] };
        }
        if (!Array.isArray(state.sharedFilesFolder.files)) {
          state.sharedFilesFolder.files = [];
        }

        const sharedFiles = state.sharedFilesFolder.files;
        if (
          !sharedFiles.some((item) => item?.id === file.id) &&
          matchesDateAndLocalFilter(
            newFile,
            state.sharedFilesFolder?.activeFilter,
          )
        ) {
          sharedFiles.unshift(newFile);
        }
      }

      // ========== FOLDER ==========
      if (folder?.id) {
        const newFolder = {
          ...folder,
          permission: permission || response.permission || "view",
        };

        // Home → Shared with me
        if (!state.homeFilesFolder?.shared_with_me) {
          state.homeFilesFolder = {
            ...state.homeFilesFolder,
            shared_with_me: { data: [] },
          };
        }
        if (!Array.isArray(state.homeFilesFolder.shared_with_me.data)) {
          state.homeFilesFolder.shared_with_me.data = [];
        }

        const sharedWithMe = state.homeFilesFolder.shared_with_me.data;
        if (!sharedWithMe.some((item) => item?.folder?.id === folder.id)) {
          sharedWithMe.unshift({
            type: "folder",
            id: folder.id,
            folder: newFolder,
          });
        }

        // Shared Folders list
        if (!state.sharedFilesFolder) {
          state.sharedFilesFolder = { files: [], folders: [] };
        }
        if (!Array.isArray(state.sharedFilesFolder.folders)) {
          state.sharedFilesFolder.folders = [];
        }

        const sharedFolders = state.sharedFilesFolder.folders;
        if (
          !sharedFolders.some((item) => item?.id === folder.id) &&
          matchesDateAndLocalFilter(
            newFolder,
            state.sharedFilesFolder?.activeFilter,
          )
        ) {
          sharedFolders.unshift(newFolder);
        }
      }
    },
    updateStorageUsed: (state, action) => {
      const { used_drive_storage, max_drive_storage } = action.payload;

      state.setting = {
        ...state.setting,
        used_drive_storage: used_drive_storage,
        max_drive_storage: max_drive_storage,
      };
    },
    restoreFilesAndFoldersGeneral: (state, action) => {
      const restoredFolders = action.payload?.folders || [];
      const restoredFiles = action.payload?.files || [];

      // Collect every file that was restored
      // (top-level files + files nested inside the restored folders)
      const allRestoredFiles = [
        ...restoredFiles,
        ...restoredFolders.flatMap((folder) => folder.files || []),
      ];

      // --------------------------------------------------
      // 1. Secure files → locked list
      // --------------------------------------------------
      const secureFiles = allRestoredFiles.filter(
        (file) => file?.is_secure === true,
      );

      if (secureFiles.length > 0) {
        if (!state.lockedFileFolder) {
          state.lockedFileFolder = { folders: [], files: [] };
        }
        if (!Array.isArray(state.lockedFileFolder.files)) {
          state.lockedFileFolder.files = [];
        }

        secureFiles.forEach((file) => {
          if (
            state.lockedFileFolder.activeFilter &&
            !matchesDateAndLocalFilter(
              file,
              state.lockedFileFolder.activeFilter,
            )
          ) {
            return;
          }

          const alreadyExists = state.lockedFileFolder.files.some(
            (f) => f.id === file.id,
          );

          if (!alreadyExists) {
            state.lockedFileFolder.files.unshift({ ...file });
          }
        });
      }

      // --------------------------------------------------
      // 2. Shared with me → Home shared_with_me
      // Ordered by last_opened_at (most recent first)
      // --------------------------------------------------
      const isSharedWithMe = (item) =>
        !!(
          item?.shared_by ||
          (item?.permission && item.permission !== "owner")
        );

      const sharedFilesToRestore = restoredFiles.filter(isSharedWithMe);
      const sharedFoldersToRestore = restoredFolders.filter(isSharedWithMe);

      if (
        sharedFilesToRestore.length > 0 ||
        sharedFoldersToRestore.length > 0
      ) {
        if (!state.homeFilesFolder) {
          state.homeFilesFolder = {};
        }
        if (!state.homeFilesFolder.shared_with_me) {
          state.homeFilesFolder.shared_with_me = { data: [], has_more: false };
        }
        if (!Array.isArray(state.homeFilesFolder.shared_with_me.data)) {
          state.homeFilesFolder.shared_with_me.data = [];
        }

        const sharedWithMe = state.homeFilesFolder.shared_with_me.data;
        const getSharedComparable = (item) => item?.file || item?.folder;

        sharedFilesToRestore.forEach((file) => {
          insertByLastOpenedAt(
            sharedWithMe,
            {
              type: "file",
              id: file.id,
              file: { ...file },
              folder: null,
            },
            getSharedComparable,
          );
        });

        sharedFoldersToRestore.forEach((folder) => {
          insertByLastOpenedAt(
            sharedWithMe,
            {
              type: "folder",
              id: folder.id,
              file: null,
              folder: { ...folder },
            },
            getSharedComparable,
          );
        });

        // has_more = true when more than 4 items
        state.homeFilesFolder.shared_with_me.has_more = sharedWithMe.length > 4;

        // Also restore into sharedFilesFolder lists
        if (state.sharedFilesFolder) {
          if (!Array.isArray(state.sharedFilesFolder.files)) {
            state.sharedFilesFolder.files = [];
          }
          if (!Array.isArray(state.sharedFilesFolder.folders)) {
            state.sharedFilesFolder.folders = [];
          }

          sharedFilesToRestore.forEach((file) => {
            if (
              !state.sharedFilesFolder.files.some((f) => f.id === file.id) &&
              matchesDateAndLocalFilter(
                file,
                state.sharedFilesFolder?.activeFilter,
              )
            ) {
              state.sharedFilesFolder.files.unshift({ ...file });
            }
          });

          sharedFoldersToRestore.forEach((folder) => {
            if (
              !state.sharedFilesFolder.folders.some(
                (f) => f.id === folder.id,
              ) &&
              matchesDateAndLocalFilter(
                folder,
                state.sharedFilesFolder?.activeFilter,
              )
            ) {
              state.sharedFilesFolder.folders.unshift({ ...folder });
            }
          });

          const { sortBy, sortOrder } = state.sortOrder || {};

          if (Array.isArray(state.sharedFilesFolder.files)) {
            state.sharedFilesFolder.files = sortItems(
              state.sharedFilesFolder.files,
              sortBy,
              sortOrder,
              true, // use file_name for files
            );
          }

          if (Array.isArray(state.sharedFilesFolder.folders)) {
            state.sharedFilesFolder.folders = sortItems(
              state.sharedFilesFolder.folders,
              sortBy,
              sortOrder,
            );
          }
        }
      }

      // --------------------------------------------------
      // 3. Starred → Home favourite_items (+ starredList)
      // --------------------------------------------------
      const starredFilesToRestore = restoredFiles.filter(
        (file) => file?.is_starred === true,
      );
      const starredFoldersToRestore = restoredFolders.filter(
        (folder) => folder?.is_starred === true,
      );

      if (
        starredFilesToRestore.length > 0 ||
        starredFoldersToRestore.length > 0
      ) {
        // Ensure favourite_items structure
        if (!state.homeFilesFolder) {
          state.homeFilesFolder = {};
        }
        if (!state.homeFilesFolder.favourite_items) {
          state.homeFilesFolder.favourite_items = { data: [], has_more: false };
        }
        if (!Array.isArray(state.homeFilesFolder.favourite_items.data)) {
          state.homeFilesFolder.favourite_items.data = [];
        }

        const favouriteItems = state.homeFilesFolder.favourite_items.data;

        // Ensure starredList structure
        if (!state.starredList) {
          state.starredList = { data: { files: [], folders: [] } };
        }
        if (!state.starredList.data) {
          state.starredList.data = { files: [], folders: [] };
        }
        if (!Array.isArray(state.starredList.data.files)) {
          state.starredList.data.files = [];
        }
        if (!Array.isArray(state.starredList.data.folders)) {
          state.starredList.data.folders = [];
        }

        // Restore starred files
        starredFilesToRestore.forEach((file) => {
          // favourite_items (home)
          const favExists = favouriteItems.some(
            (item) => item?.file?.id === file.id || item?.id === file.id,
          );
          if (!favExists) {
            favouriteItems.unshift({
              type: "file",
              id: file.id,
              file: { ...file, is_starred: true },
            });
          }

          // starredList – safe filter check
          const alreadyExists = state.starredList.data.files.some(
            (f) => f.id === file.id,
          );
          if (
            !alreadyExists &&
            (!state.starredList?.activeFilter ||
              matchesDateAndLocalFilter(file, state.starredList.activeFilter))
          ) {
            state.starredList.data.files.unshift({
              ...file,
              is_starred: true,
            });
          }
        });

        // Restore starred folders
        starredFoldersToRestore.forEach((folder) => {
          // favourite_items (home)
          const favExists = favouriteItems.some(
            (item) => item?.folder?.id === folder.id || item?.id === folder.id,
          );
          if (!favExists) {
            favouriteItems.unshift({
              type: "folder",
              id: folder.id,
              folder: { ...folder, is_starred: true },
            });
          }

          // starredList – safe filter check
          const alreadyExists = state.starredList.data.folders.some(
            (f) => f.id === folder.id,
          );
          if (
            !alreadyExists &&
            (!state.starredList?.activeFilter ||
              matchesDateAndLocalFilter(folder, state.starredList.activeFilter))
          ) {
            state.starredList.data.folders.unshift({
              ...folder,
              is_starred: true,
            });
          }
        });

        // has_more = true when more than 4 items
        state.homeFilesFolder.favourite_items.has_more =
          favouriteItems.length > 4;

        // Keep starredList sorted with the CURRENT sort order
        const { sortBy, sortOrder } = state.sortOrder || {};
        if (Array.isArray(state.starredList.data.files)) {
          state.starredList.data.files = sortItems(
            state.starredList.data.files,
            sortBy,
            sortOrder,
            true, // use file_name for files
          );
        }
        if (Array.isArray(state.starredList.data.folders)) {
          state.starredList.data.folders = sortItems(
            state.starredList.data.folders,
            sortBy,
            sortOrder,
          );
        }
      }

      // --------------------------------------------------
      // 4. Restored files → recentFilesFolder
      // Only files (not folders) that have last_opened_at set
      // --------------------------------------------------
      const recentEligibleFiles = restoredFiles?.filter(
        (file) => file?.last_opened_at != null,
      );

      if (recentEligibleFiles.length > 0) {
        const validFiles = recentEligibleFiles?.filter((file) =>
          matchesDateAndLocalFilter(
            file,
            state.recentFilesFolder?.activeFilter,
          ),
        );

        validFiles?.forEach((file) => {
          const alreadyExists = state.recentFilesFolder?.data?.some(
            (f) => f.id === file.id,
          );
          if (!alreadyExists) {
            state.recentFilesFolder?.data?.unshift({ ...file });
            state.recentFilesFolder = {
              ...state.recentFilesFolder,
              total: (state.recentFilesFolder?.total || 0) + 1,
            };
          }
        });
      }

      // --------------------------------------------------
      // 5. Restored owned folders → Home my_folders
      // Ordered by last_opened_at (most recent first).
      // Only folders that have last_opened_at set (not null)
      // and are not shared-with-me items.
      // --------------------------------------------------
      const ownedFoldersToRestore = restoredFolders.filter(
        (folder) => folder?.last_opened_at != null && !isSharedWithMe(folder),
      );

      if (ownedFoldersToRestore.length > 0) {
        if (!state.homeFilesFolder) {
          state.homeFilesFolder = {
            my_folders: { data: [], has_more: false },
          };
        }
        if (!state.homeFilesFolder.my_folders) {
          state.homeFilesFolder.my_folders = { data: [], has_more: false };
        }
        if (!Array.isArray(state.homeFilesFolder.my_folders.data)) {
          state.homeFilesFolder.my_folders.data = [];
        }

        const myFolders = state.homeFilesFolder.my_folders.data;

        ownedFoldersToRestore.forEach((folder) => {
          if (
            !matchesDateAndLocalFilter(
              folder,
              state.homeFilesFolder?.activeFilter,
            )
          ) {
            return;
          }

          // If already present, refresh data and re-position by last_opened_at
          const existingIndex = myFolders.findIndex((f) => f?.id === folder.id);
          if (existingIndex !== -1) {
            myFolders.splice(existingIndex, 1);
          }

          insertByLastOpenedAt(myFolders, { ...folder });
        });

        state.homeFilesFolder.my_folders.has_more = myFolders.length > 4;
      }

      // --------------------------------------------------
      // 6. Restored owned files → Home my_files
      // Ordered by last_opened_at (most recent first).
      // Only files that have last_opened_at set (not null)
      // and are not shared-with-me items.
      // --------------------------------------------------
      const ownedFilesToRestore = restoredFiles.filter(
        (file) => file?.last_opened_at != null && !isSharedWithMe(file),
      );

      if (ownedFilesToRestore.length > 0) {
        if (!state.homeFilesFolder) {
          state.homeFilesFolder = {
            my_files: { data: [], has_more: false },
          };
        }
        if (!state.homeFilesFolder.my_files) {
          state.homeFilesFolder.my_files = { data: [], has_more: false };
        }
        if (!Array.isArray(state.homeFilesFolder.my_files.data)) {
          state.homeFilesFolder.my_files.data = [];
        }

        const myFiles = state.homeFilesFolder.my_files.data;

        ownedFilesToRestore.forEach((file) => {
          if (
            !matchesDateAndLocalFilter(
              file,
              state.homeFilesFolder?.activeFilter,
            )
          ) {
            return;
          }

          // If already present, refresh data and re-position by last_opened_at
          const existingIndex = myFiles.findIndex((f) => f?.id === file.id);
          if (existingIndex !== -1) {
            myFiles.splice(existingIndex, 1);
          }

          insertByLastOpenedAt(myFiles, { ...file });
        });

        state.homeFilesFolder.my_files.has_more = myFiles.length > 4;
      }
    },
    restoreFilesOpenFile: (state, action) => {
      const restoredFiles = action.payload?.files || [];
      if (!restoredFiles.length) return;

      const restoredFileIds = restoredFiles.map((file) => file.id);

      if (restoredFileIds.includes(state?.openFile?.id)) {
        state.openFile = { ...state.openFile, is_trashed: false };
      }
    },
    permanentDeleteFilesOpenFile: (state, action) => {
      const deletedFiles = action.payload?.files || [];
      const deletedFileIds = deletedFiles.map((file) => file.id);

      if (state.openFile && deletedFileIds.includes(state.openFile.id)) {
        // Keep the object, just mark it so UI can hide Download + FileMenu
        state.openFile = {
          ...state.openFile,
          is_trashed: true,
          is_permanently_deleted: true,
        };
      }
    },

    emptyTrashOpenFile: (state) => {
      if (state.openFile?.is_trashed) {
        state.openFile = {
          ...state.openFile,
          is_permanently_deleted: true,
        };
      }
    },
  },
  extraReducers(builder) {
    builder

      //Get static Pages
      .addCase(GetStaticPagesAction.pending, (state) => {
        state.staticPagesLoading = true;
        state.staticPages = null;
      })
      .addCase(GetStaticPagesAction.fulfilled, (state, action) => {
        state.staticPagesLoading = false;
        state.staticPages = action.payload.response;
      })
      .addCase(GetStaticPagesAction.rejected, (state) => {
        state.staticPagesLoading = false;
        state.staticPages = null;
      })
      .addCase(GetHomeFilesFolderAction.pending, (state) => {
        state.homeFilesFolderLoading =
          state.homeFilesFolder !== null ? false : true;
        state.homeFilesFolder =
          state.homeFilesFolder !== null ? state.homeFilesFolder : null;
      })
      .addCase(GetHomeFilesFolderAction.fulfilled, (state, action) => {
        state.homeFilesFolder = action.payload.response;
        state.homeFilesFolderLoading = false;
      })
      .addCase(GetHomeFilesFolderAction.rejected, (state) => {
        state.homeFilesFolderLoading = false;
      })
      .addCase(GetSettingAction.pending, (state) => {
        state.settingLoading = true;
      })
      .addCase(GetSettingAction.fulfilled, (state, action) => {
        state.setting = action.payload.response;
        state.settingLoading = false;
      })
      .addCase(GetSettingAction.rejected, (state) => {
        state.settingLoading = false;
      })

      .addCase(GetRecentFilesFolderAction.pending, (state) => {
        state.recentFilesFolderLoading =
          state.recentFilesFolder?.total > 0 ? false : true;
        state.recentFilesFolder =
          state.recentFilesFolder?.total > 0 ? state.recentFilesFolder : null;
      })
      .addCase(GetRecentFilesFolderAction.fulfilled, (state, action) => {
        // state.recentFilesFolder = action.payload.response;
        state.recentFilesFolderLoading = false;

        const newData = action.payload?.response;
        if (newData?.page === 1) {
          state.recentFilesFolder = {
            ...newData,
            activeFilter: action.meta.arg,
          };
        } else {
          const newItems = [
            ...(state.recentFilesFolder?.data || []),
            ...(newData?.data || []),
          ];
          state.recentFilesFolder = {
            ...newData,
            activeFilter: action.meta.arg,
            data: newItems,
          };
        }
      })
      .addCase(GetRecentFilesFolderAction.rejected, (state) => {
        state.recentFilesFolderLoading = false;
      })

      .addCase(SearchFilesFolderAction.pending, (state) => {
        state.searchFilesFolderLoading = state.searchFilesFolder ? false : true;
      })
      .addCase(SearchFilesFolderAction.fulfilled, (state, action) => {
        // state.searchFilesFolder = action.payload.response;
        state.searchFilesFolderLoading = false;

        const newData = action.payload?.response;
        if (newData.page === 1) {
          state.searchFilesFolder = newData;
        } else {
          state.searchFilesFolder = {
            ...newData,
            folders: [
              ...(state.searchFilesFolder?.folders || []),
              ...(newData?.folders || []),
            ],
            files: [
              ...(state.searchFilesFolder?.files || []),
              ...(newData?.files || []),
            ],
          };
        }
      })
      .addCase(SearchFilesFolderAction.rejected, (state) => {
        state.searchFilesFolderLoading = false;
      })

      .addCase(UpdateFolderAction.fulfilled, (state, action) => {
        const updatedFolder = action.payload.response;

        [
          state.sharedFilesFolder?.folders,
          state.starredList?.data?.folders,
          state.lockedFileFolder?.folders,
          state.searchFilesFolder?.folders,
          state.recentFilesFolder?.data,
          state.homeFilesFolder?.my_folders?.data,
        ]
          .filter(Boolean)
          .forEach((list) => {
            const folder = list?.find((f) => f.id === updatedFolder.id);
            if (folder) folder.name = updatedFolder.name;
          });
        [
          state.homeFilesFolder?.shared_with_me?.data,
          state.homeFilesFolder?.favourite_items?.data,
        ]
          .filter(Boolean)
          .forEach((list) => {
            const folder = list?.find((f) => f.id === updatedFolder.id);
            if (folder) folder.folder.name = updatedFolder.name;
          });
      })

      .addCase(RenameFilesAction.fulfilled, (state, action) => {
        const updatedFile = action.payload.response;

        [
          state.sharedFilesFolder?.files,
          state.searchFilesFolder?.files,
          state.lockedFileFolder?.files,
          state.starredList?.data?.files,
          state.recentFilesFolder?.data,
          state.homeFilesFolder?.my_files?.data,
          state.openFile && [state.openFile],
        ]
          .filter(Boolean)
          .forEach((list) => {
            const file = list?.find((f) => f.id === updatedFile.id);
            if (file) file.file_name = updatedFile.file_name;
          });
        [
          state.homeFilesFolder?.shared_with_me?.data,
          state.homeFilesFolder?.favourite_items?.data,
        ]
          .filter(Boolean)
          .forEach((list) => {
            const file = list?.find((f) => f.id === updatedFile.id);
            if (file) file.file.file_name = updatedFile.file_name;
          });
      })

      // Move File To Trash

      // .addCase(TrashFilesFolderAction.fulfilled, (state, action) => {
      //   const trashedFolders = action.payload?.response?.folders || [];
      //   const trashedFiles = action.payload?.response?.files || [];

      //   const trashedFolderIds = trashedFolders.map((f) => f.id);
      //   const trashedFileIds = trashedFiles.map((f) => f.id);

      //   const allTrashedIds = [...trashedFolderIds, ...trashedFileIds];

      //   if (allTrashedIds.length === 0) return;

      //   const removeByIds = (list, ids) =>
      //     list?.filter((item) => !ids.includes(item.id));

      //   // Update recentFilesFolder - handle object structure properly
      //   if (state.recentFilesFolder?.data) {
      //     const updatedData = removeByIds(
      //       state.recentFilesFolder.data,
      //       allTrashedIds,
      //     );
      //     state.recentFilesFolder.data = updatedData;
      //     state.recentFilesFolder.total = updatedData.length;
      //   }

      //   // Update homeFilesFolder
      //   if (state.homeFilesFolder?.my_folders?.data) {
      //     state.homeFilesFolder.my_folders.data = removeByIds(
      //       state.homeFilesFolder.my_folders.data,
      //       allTrashedIds,
      //     );
      //   }

      //   if (state.homeFilesFolder?.my_files?.data) {
      //     state.homeFilesFolder.my_files.data = removeByIds(
      //       state.homeFilesFolder.my_files.data,
      //       allTrashedIds,
      //     );
      //   }

      //   // Update searchFilesFolder
      //   if (state.searchFilesFolder?.folders) {
      //     state.searchFilesFolder.folders = removeByIds(
      //       state.searchFilesFolder.folders,
      //       allTrashedIds,
      //     );
      //   }

      //   if (state.searchFilesFolder?.files) {
      //     state.searchFilesFolder.files = removeByIds(
      //       state.searchFilesFolder.files,
      //       allTrashedIds,
      //     );
      //   }

      //   if (state.lockedFileFolder?.folders) {
      //     state.lockedFileFolder.folders = removeByIds(
      //       state.lockedFileFolder.folders,
      //       allTrashedIds,
      //     );
      //   }

      //   if (state.lockedFileFolder?.files) {
      //     state.lockedFileFolder.files = removeByIds(
      //       state.lockedFileFolder.files,
      //       allTrashedIds,
      //     );
      //   }

      //   if (state.starredList?.data?.folders) {
      //     state.starredList.data.folders = removeByIds(
      //       state.starredList.data.folders,
      //       allTrashedIds,
      //     );
      //   }

      //   if (state.starredList?.data?.files) {
      //     state.starredList.data.files = removeByIds(
      //       state.starredList.data.files,
      //       allTrashedIds,
      //     );
      //   }
      // })

      // Get help And Feedback
      .addCase(GetHelpAndFeedbackAction.pending, (state) => {
        state.helpAndFeedbackLoading =
          state.helpAndFeedback?.list?.length > 0 ? false : true;
        state.helpAndFeedback =
          state.helpAndFeedback?.list?.length > 0
            ? state.helpAndFeedback
            : null;
      })
      .addCase(GetHelpAndFeedbackAction.fulfilled, (state, action) => {
        state.helpAndFeedbackLoading = false;
        state.helpAndFeedback = action.payload.response;
      })
      .addCase(GetHelpAndFeedbackAction.rejected, (state) => {
        state.helpAndFeedbackLoading = false;
        state.helpAndFeedback = null;
      })

      // Get Locked file folder
      .addCase(GetLockedFileFolderListAction.pending, (state) => {
        state.lockedFileFolderLoading =
          state.lockedFileFolder?.total > 0 ? false : true;
        state.lockedFileFolder =
          state.lockedFileFolder?.total > 0 ? state.lockedFileFolder : null;
      })
      .addCase(GetLockedFileFolderListAction.fulfilled, (state, action) => {
        state.lockedFileFolderLoading = false;

        const newData = action.payload?.response;
        if (newData?.page === 1) {
          state.lockedFileFolder = {
            ...newData,
            activeFilter: action.meta.arg,
          };
        } else {
          state.lockedFileFolder = {
            ...newData,
            activeFilter: action.meta.arg,
            folders: [
              ...(state.lockedFileFolder?.folders || []),
              ...(newData?.folders || []),
            ],
            files: [
              ...(state.lockedFileFolder?.files || []),
              ...(newData?.files || []),
            ],
          };
        }
      })
      .addCase(GetLockedFileFolderListAction.rejected, (state) => {
        state.lockedFileFolderLoading = false;
        state.lockedFileFolder = null;
      })

      // Get starred file folder
      .addCase(GetStarredFileFolderListAction.pending, (state) => {
        state.starredListLoading = state.starredList?.total > 0 ? false : true;
        state.starredList =
          state.starredList?.total > 0 ? state.starredList : null;
      })
      .addCase(GetStarredFileFolderListAction.fulfilled, (state, action) => {
        state.starredListLoading = false;

        const newData = action.payload?.response;
        if (newData?.page === 1) {
          state.starredList = {
            ...newData,
            activeFilter: action.meta.arg,
          };
        } else {
          state.starredList = {
            ...newData,
            activeFilter: action.meta.arg,
            data: {
              folders: [
                ...(state.starredList?.data?.folders || []),
                ...(newData?.data?.folders || []),
              ],
              files: [
                ...(state.starredList?.data?.files || []),
                ...(newData?.data?.files || []),
              ],
            },
          };
        }
      })
      .addCase(GetStarredFileFolderListAction.rejected, (state) => {
        state.starredListLoading = false;
      })

      .addCase(StarUnstarFileFolderAction.fulfilled, (state) => {
        // Reset loading flag
        state.starredListLoading = false;
        // const isStarred = action.meta.arg.isStarred;
        // const { files = [], folders = [] } = action.payload.response || {};

        // // Use IDs from the response if available, otherwise fallback to the IDs sent in the request.
        // // This is necessary because when unstarring, the response might not include the full file objects.
        // const fileIds =
        //   files.length > 0
        //     ? files.map((f) => f.id)
        //     : action.meta.arg.fileIds || [];
        // const folderIds =
        //   folders.length > 0
        //     ? folders.map((f) => f.id)
        //     : action.meta.arg.folderIds || [];

        // // Ensure starredList exists
        // if (!state.starredList) {
        //   state.starredList = { data: { files: [], folders: [] } };
        // }
        // if (!state.starredList.data) {
        //   state.starredList.data = { files: [], folders: [] };
        // }

        // // Remove any existing entries for these ids to avoid duplicates
        // state.starredList.data.files = state.starredList.data.files.filter(
        //   (f) => !fileIds.includes(f.id),
        // );
        // state.starredList.data.folders = state.starredList.data.folders.filter(
        //   (f) => !folderIds.includes(f.id),
        // );

        // // Prepare new starred items
        // const newItems = isStarred
        //   ? [
        //     ...files.map((f) => ({
        //       type: "file",
        //       id: f.id,
        //       file: { ...f, is_starred: true },
        //     })),
        //     ...folders.map((f) => ({
        //       type: "folder",
        //       id: f.id,
        //       folder: { ...f, is_starred: true },
        //     })),
        //   ]
        //   : [];

        // // Update favourite items in home view (used for UI display)
        // if (state.homeFilesFolder) {
        //   if (isStarred) {
        //     state.homeFilesFolder.favourite_items.data = [
        //       ...newItems,
        //       ...(state.homeFilesFolder.favourite_items?.data || []),
        //     ];
        //   } else {
        //     // Remove unstarred items from favourite list
        //     state.homeFilesFolder.favourite_items.data = (
        //       state.homeFilesFolder.favourite_items?.data || []
        //     ).filter((item) => {
        //       return (
        //         !(item.file && fileIds.includes(item.file.id)) &&
        //         !(item.folder && folderIds.includes(item.folder.id))
        //       );
        //     });
        //   }
        // }

        // // Update is_starred flag across all relevant lists
        // const listsToUpdate = [
        //   state.homeFilesFolder?.my_files?.data,
        //   state.homeFilesFolder?.my_folders?.data,
        //   state.recentFilesFolder?.data,
        //   state.searchFilesFolder?.files,
        //   state.searchFilesFolder?.folders,
        //   state.sharedFilesFolder?.files,
        //   state.sharedFilesFolder?.folders,
        //   state.lockedFileFolder?.files,
        //   state.lockedFileFolder?.folders,
        //   state.openFile && [state.openFile],
        // ];
        // listsToUpdate?.forEach((list) => {
        //   list?.forEach((item) => {
        //     if (item.id && fileIds.includes(item.id)) {
        //       item.is_starred = !item.is_starred;
        //     }
        //     if (item.id && folderIds.includes(item.id)) {
        //       item.is_starred = !item.is_starred;
        //     }
        //   });
        // });

        // // Update is_starred flag in shared_with_me (nested structure)
        // if (state.homeFilesFolder?.shared_with_me?.data) {
        //   state.homeFilesFolder.shared_with_me.data.forEach((item) => {
        //     if (item.file && fileIds.includes(item.file.id)) {
        //       item.file.is_starred = !item.file.is_starred;
        //     }
        //     if (item.folder && folderIds.includes(item.folder.id)) {
        //       item.folder.is_starred = !item.folder.is_starred;
        //     }
        //   });
        // }
      })

      // Lock / Unlock file folder — remove unlocked items from lockedFileFolder immediately
      .addCase(LockUnlockFileFolderAction.fulfilled, (state, action) => {
        const {
          folderIds = [],
          fileIds = [],
          action: lockAction,
        } = action.meta.arg || {};

        // Only remove from the locked list when the action is "unsecure" (unlock)
        if (lockAction === "unsecure" && state.lockedFileFolder) {
          if (state.lockedFileFolder.folders) {
            state.lockedFileFolder.folders =
              state.lockedFileFolder.folders.filter(
                (folder) => !folderIds.includes(folder.id),
              );
          }
          if (state.lockedFileFolder.files) {
            state.lockedFileFolder.files = state.lockedFileFolder.files.filter(
              (file) => !fileIds.includes(file.id),
            );
          }
        }

        const isSecure = lockAction === "secure" ? true : false;

        // Update folders
        [
          state.sharedFilesFolder?.folders,
          state.searchFilesFolder?.folders,
          state.recentFilesFolder?.data,
          state.homeFilesFolder?.my_folders?.data,
          state.starredList?.data?.folders,
        ]
          .filter(Boolean)
          .forEach((list) => {
            list.forEach((folder) => {
              if (folderIds.includes(folder.id)) folder.is_secure = isSecure;
            });
          });

        [
          state.homeFilesFolder?.shared_with_me?.data,
          state.homeFilesFolder?.favourite_items?.data,
        ]
          .filter(Boolean)
          .forEach((list) => {
            list.forEach((item) => {
              if (item.folder && folderIds.includes(item.folder.id)) {
                item.folder.is_secure = isSecure;
              }
            });
          });

        // Update files
        [
          state.sharedFilesFolder?.files,
          state.searchFilesFolder?.files,
          state.recentFilesFolder?.data,
          state.homeFilesFolder?.my_files?.data,
          state.starredList?.data?.files,
          state.openFile && [state.openFile],
        ]
          .filter(Boolean)
          .forEach((list) => {
            list.forEach((file) => {
              if (fileIds.includes(file.id)) file.is_secure = isSecure;
            });
          });

        [
          state.homeFilesFolder?.shared_with_me?.data,
          state.homeFilesFolder?.favourite_items?.data,
        ]
          .filter(Boolean)
          .forEach((list) => {
            list.forEach((item) => {
              if (item.file && fileIds.includes(item.file.id)) {
                item.file.is_secure = isSecure;
              }
            });
          });
      })

      // .addCase(UploadFilesAction.pending, (state) => {
      //   state.uploadFileLoading = true;
      //   state.uploadFileError = false;
      // })

      // .addCase(UploadFilesAction.fulfilled, (state, action) => {
      //   state.uploadFileLoading = false;
      //   state.uploadFileError = false;

      //   const responseError = action.payload?.status === 0 ? true : false;
      //   const uploaded = action.payload?.response?.uploaded || [];
      //   const failed = action.payload?.response?.failed || [];
      //   const tempIds = action.meta.arg?.tempIds || [];

      //   // Update status of existing entries based on tempIds
      //   uploaded.forEach((file, index) => {
      //     const tempId = tempIds[index];
      //     if (tempId) {
      //       const existing = state.uploadNewFile.list.find(
      //         (f) => f.tempId === tempId,
      //       );
      //       if (existing) {
      //         existing.status = "uploaded";
      //       } else {
      //         // If not found, add new entry
      //         state.uploadNewFile.list.push({
      //           tempId,
      //           name: file.name || file.file_name || "",
      //           status: "uploaded",
      //         });
      //       }
      //     }
      //   });
      //   // Handle failed files
      //   failed.forEach((file, index) => {
      //     const tempId = tempIds[uploaded.length + index];
      //     if (tempId) {
      //       const existing = state.uploadNewFile.list.find(
      //         (f) => f.tempId === tempId,
      //       );
      //       if (existing) {
      //         existing.status = "failed";
      //       } else {
      //         state.uploadNewFile.list.push({
      //           tempId,
      //           name: file.fileName || "",
      //           status: "failed",
      //         });
      //       }
      //     }
      //   });
      //   // Ensure display flag is true if there are any entries
      //   state.uploadNewFile.display = state.uploadNewFile.list.length > 0;
      //   // Update recent and home folders with newly uploaded files
      //   if (uploaded.length > 0 && state.recentFilesFolder?.data) {
      //     state.recentFilesFolder.data = [
      //       ...uploaded,
      //       ...state.recentFilesFolder.data,
      //     ];
      //   }
      //   if (uploaded.length > 0 && state.homeFilesFolder?.my_files?.data) {
      //     state.homeFilesFolder.my_files.data = [
      //       ...uploaded,
      //       ...state.homeFilesFolder.my_files.data,
      //     ];
      //   }
      //   // Set error flag if any failures
      //   state.uploadFileError = failed.length > 0;
      //   // If response error, clear the UI list
      //   if (responseError) {
      //     state.uploadNewFile = { list: [], display: false };
      //   }
      // })

      // .addCase(UploadFilesAction.rejected, (state, action) => {
      //   state.uploadFileLoading = false;
      //   // Upload was cancelled
      //   if (action.payload?.canceled) {
      //     return;
      //   }

      //   state.uploadFileError = true;
      //   state.uploadNewFile = {
      //     list: [],
      //     display: false,
      //   };
      // })

      .addCase(UploadFilesAction.pending, (state, action) => {
        state.uploadFileLoading = true;
        state.uploadFileError = false;
        const { tempId } = action.meta.arg || {};
        const file = state.uploadNewFile.list.find((f) => f.tempId === tempId);
        if (file) {
          file.status = "uploading";
          file.progress = 0;
        }
      })

      .addCase(UploadFilesAction.fulfilled, (state, action) => {
        const { tempId, oldFileId } = action.payload || {};
        const file = state.uploadNewFile.list.find((f) => f.tempId === tempId);

        if (file) {
          file.status = "uploaded";
          file.progress = 100;
        }

        // Remove old file from state if this was a replacement
        if (oldFileId) {
          const removeById = (list, id) =>
            list?.filter((item) => item.id !== id);

          // Update recentFilesFolder
          if (state.recentFilesFolder?.data) {
            state.recentFilesFolder.data = removeById(
              state.recentFilesFolder.data,
              oldFileId,
            );
            state.recentFilesFolder.total = state.recentFilesFolder.data.length;
          }

          // Update homeFilesFolder
          if (state.homeFilesFolder?.my_files?.data) {
            state.homeFilesFolder.my_files.data = removeById(
              state.homeFilesFolder.my_files.data,
              oldFileId,
            );
          }

          // Update searchFilesFolder
          if (state.searchFilesFolder?.files) {
            state.searchFilesFolder.files = removeById(
              state.searchFilesFolder.files,
              oldFileId,
            );
          }

          // Update sharedFilesFolder
          if (state.sharedFilesFolder?.files) {
            state.sharedFilesFolder.files = removeById(
              state.sharedFilesFolder.files,
              oldFileId,
            );
          }

          // Update lockedFileFolder
          if (state.lockedFileFolder?.files) {
            state.lockedFileFolder.files = removeById(
              state.lockedFileFolder.files,
              oldFileId,
            );
          }

          // Update starredList
          if (state.starredList?.data?.files) {
            state.starredList.data.files = removeById(
              state.starredList.data.files,
              oldFileId,
            );
          }

          // Update nested file objects in Home (shared_with_me, favourite_items)
          const nestedLists = [
            state.homeFilesFolder?.shared_with_me?.data,
            state.homeFilesFolder?.favourite_items?.data,
          ];

          nestedLists.forEach((list) => {
            if (list) {
              for (let i = list.length - 1; i >= 0; i--) {
                if (list[i]?.file?.id === oldFileId) {
                  list.splice(i, 1);
                }
              }
            }
          });
        }
        // if (response) {
        //   if (state.recentFilesFolder?.data) {
        //     state.recentFilesFolder.data = [
        //       ...response.uploaded,
        //       ...state.recentFilesFolder.data,
        //     ];
        //   }
        //   if (state.homeFilesFolder?.my_files?.data) {
        //     state.homeFilesFolder.my_files.data = [
        //       ...response.uploaded,
        //       ...state.homeFilesFolder.my_files.data,
        //     ];
        //   }
        // }
      })

      .addCase(UploadFilesAction.rejected, (state, action) => {
        const { tempId, canceled } = action.payload || {};
        const file = state.uploadNewFile.list.find((f) => f.tempId === tempId);

        if (file) {
          if (file.status !== "canceled") {
            file.status = canceled ? "canceled" : "failed";
          }
        }

        state.uploadFileLoading = state.uploadNewFile.list.some(
          (f) => f.status === "uploading",
        );

        if (!canceled) {
          state.uploadFileError = true;
        }
      })

      .addCase(GetSharedFilesFolderAction.pending, (state) => {
        state.sharedFilesFolderLoading =
          state.sharedFilesFolder?.total > 0 ? false : true;
        state.sharedFilesFolder =
          state.sharedFilesFolder?.total > 0 ? state.sharedFilesFolder : null;
      })
      .addCase(GetSharedFilesFolderAction.fulfilled, (state, action) => {
        state.sharedFilesFolderLoading = false;
        const newData = action.payload?.response;
        if (newData.page === 1) {
          state.sharedFilesFolder = {
            ...newData,
            activeFilter: action.meta.arg,
          };
        } else {
          state.sharedFilesFolder = {
            ...newData,
            activeFilter: action.meta.arg,
            folders: [
              ...state.sharedFilesFolder.folders,
              ...(newData?.folders || []),
            ],
            files: [
              ...state.sharedFilesFolder.files,
              ...(newData?.files || []),
            ],
          };
        }
      })
      .addCase(GetSharedFilesFolderAction.rejected, (state) => {
        state.sharedFilesFolderLoading = false;
      })

      // Open File
      .addCase(GetOpenFileAction.pending, (state) => {
        state.openFileLoading = true;
        state.openFile = null;
      })
      .addCase(GetOpenFileAction.fulfilled, (state, action) => {
        state.openFileLoading = false;
        state.openFile = action.payload.response;
      })
      .addCase(GetOpenFileAction.rejected, (state, action) => {
        state.openFileLoading = false;
        state.openFile = action.payload?.response;
      })

      //Get Language
      .addCase(GetLanguageAction.pending, (state) => {
        state.languageLoading = true;
        state.language = [];
      })
      .addCase(GetLanguageAction.fulfilled, (state, action) => {
        state.languageLoading = false;
        state.language = action.payload.response;
      })
      .addCase(GetLanguageAction.rejected, (state) => {
        state.languageLoading = false;
        state.language = [];
      })

      // People Suggestion
      .addCase(PeopleSuggestionAction.pending, (state) => {
        state.peopleSuggestionLoading = true;
        state.peopleSuggestion = [];
      })
      .addCase(PeopleSuggestionAction.fulfilled, (state, action) => {
        state.peopleSuggestionLoading = false;
        state.peopleSuggestion = action.payload.response;
      })
      .addCase(PeopleSuggestionAction.rejected, (state) => {
        state.peopleSuggestionLoading = false;
        state.peopleSuggestion = [];
      })

      // .addCase(UpdatePermissionsFileFolderAction.fulfilled, (state, action) => {
      //   const { profileId, folder_id, file_id, user_id, permission } =
      //     action.payload;

      //   const list = [
      //     state.homeFilesFolder?.my_files?.data,
      //     state.homeFilesFolder?.my_folders?.data,
      //     state.starredList?.data?.files,
      //     state.starredList?.data?.folders,
      //     state.sharedFilesFolder?.files,
      //     state.sharedFilesFolder?.folders,
      //     state.recentFilesFolder?.data,
      //     state.searchFilesFolder?.files,
      //     state.searchFilesFolder?.folders,
      //     state.openFile && [state.openFile],
      //   ];

      //   list.forEach((l) => {
      //     l?.forEach((item) => {
      //       if (
      //         (item.id === file_id || item.id === folder_id) &&
      //         user_id === profileId
      //       ) {
      //         item.permission = permission;
      //       }
      //     });
      //   });

      //   const sharedList = [
      //     state.homeFilesFolder?.shared_with_me?.data,
      //     state.homeFilesFolder?.favourite_items?.data,
      //   ];

      //   sharedList.forEach((l) => {
      //     l?.forEach((item) => {
      //       if (
      //         (item.id === file_id || item.id === folder_id) &&
      //         user_id === profileId
      //       ) {
      //         if (folder_id) {
      //           item.folder.permission = permission;
      //         } else if (file_id) {
      //           item.file.permission = permission;
      //         }
      //       }
      //     });
      //   });
      // })
      // Create share link — update general_access on every file/folder list
      .addCase(CreateShareLinkFileFolderAction.fulfilled, (state, action) => {
        const { shared_link, access_link } = action.payload?.response || {};
        if (!shared_link) return;

        const folderId = action.meta.arg?.folderId;
        const fileId = action.meta.arg?.fileId;

        const generalAccess = {
          visibility: shared_link.visibility,
          role: shared_link.role,
          shared_link: access_link,
          expires_at: shared_link.expires_at,
        };

        // Helper: update general_access on a flat list of files/folders
        const updateList = (list) => {
          if (!list) return;
          list.forEach((item) => {
            if (
              (fileId && item?.id === fileId) ||
              (folderId && item?.id === folderId)
            ) {
              item.general_access = generalAccess;
            }
          });
        };

        // Helper: update nested file/folder objects (shared_with_me, favourite_items)
        const updateNestedList = (list) => {
          if (!list) return;
          list.forEach((item) => {
            if (fileId && item?.file?.id === fileId) {
              item.file.general_access = generalAccess;
            }
            if (folderId && item?.folder?.id === folderId) {
              item.folder.general_access = generalAccess;
            }
          });
        };

        // Flat lists
        updateList(state.homeFilesFolder?.my_files?.data);
        updateList(state.homeFilesFolder?.my_folders?.data);
        updateList(state.recentFilesFolder?.data);
        updateList(state.sharedFilesFolder?.files);
        updateList(state.sharedFilesFolder?.folders);
        updateList(state.searchFilesFolder?.files);
        updateList(state.searchFilesFolder?.folders);
        updateList(state.lockedFileFolder?.files);
        updateList(state.lockedFileFolder?.folders);
        updateList(state.starredList?.data?.files);
        updateList(state.starredList?.data?.folders);

        // Nested lists
        updateNestedList(state.homeFilesFolder?.shared_with_me?.data);
        updateNestedList(state.homeFilesFolder?.favourite_items?.data);

        // Open / public file & folder
        if (fileId && state.openFile?.id === fileId) {
          state.openFile.general_access = generalAccess;
        }
        if (fileId && state.publicFile?.id === fileId) {
          state.publicFile.general_access = generalAccess;
        }
        if (folderId && state.publicFolder?.id === folderId) {
          state.publicFolder.general_access = generalAccess;
        }
      })

      //Get all review list
      .addCase(GetAllReviewListAction.pending, (state) => {
        state.reviewListLoading = true;
        state.reviewList = [];
      })
      .addCase(GetAllReviewListAction.fulfilled, (state, action) => {
        state.reviewListLoading = false;
        state.reviewList = action.payload.response;
      })
      .addCase(GetAllReviewListAction.rejected, (state) => {
        state.reviewListLoading = false;
        state.reviewList = [];
      })

      .addCase(MoveFileAction.fulfilled, (state, action) => {
        if (state.openFile) {
          state.openFile.folder_id = action.meta.arg.parentId;
        }
      })

      .addCase(GetOpenFilePublicAction.pending, (state) => {
        state.publicFileLoading = true;
        state.publicFile = null;
      })
      .addCase(GetOpenFilePublicAction.fulfilled, (state, action) => {
        state.publicFileLoading = false;
        state.publicFile = action.payload.response;
      })
      .addCase(GetOpenFilePublicAction.rejected, (state) => {
        state.publicFileLoading = false;
        state.publicFile = null;
      })

      .addCase(GetOpenPublicFolderAction.pending, (state) => {
        state.publicFolderLoading = true;
        state.publicFolder = null;
      })
      .addCase(GetOpenPublicFolderAction.fulfilled, (state, action) => {
        state.publicFolderLoading = false;
        state.publicFolder = action.payload.response;
      })
      .addCase(GetOpenPublicFolderAction.rejected, (state) => {
        state.publicFolderLoading = false;
        state.publicFolder = null;
      })

      //search history
      .addCase(GetSearchHistoryAction.pending, (state) => {
        state.searchHistoryLoading = true;
        state.searchHistory = null;
      })
      .addCase(GetSearchHistoryAction.fulfilled, (state, action) => {
        state.searchHistoryLoading = false;
        state.searchHistory = action.payload.response;
      })
      .addCase(GetSearchHistoryAction.rejected, (state) => {
        state.searchHistoryLoading = false;
        state.searchHistory = null;
      })
      // Clear search history — type: "all" | type: "single" + id
      .addCase(ClearSearchHistoryAction.fulfilled, (state, action) => {
        const { type, id } = action.meta.arg || {};

        if (type === "all") {
          state.searchHistory = [];
          return;
        }

        if (
          type === "single" &&
          id != null &&
          Array.isArray(state.searchHistory)
        ) {
          state.searchHistory = state.searchHistory.filter(
            (item) => item.id !== id,
          );
        }
      })

      // Add search history — payload: { title: "..." }
      .addCase(AddSearchHistoryAction.fulfilled, (state, action) => {
        const newItem = action.payload?.response;

        // API returns the created item { title, id }
        if (newItem?.id && newItem?.title) {
          if (!Array.isArray(state.searchHistory)) {
            state.searchHistory = [newItem];
            return;
          }

          const existingIndex = state.searchHistory.findIndex(
            (item) => item.id === newItem.id,
          );

          if (existingIndex === -1) {
            // New item → add to top
            state.searchHistory = [newItem, ...state.searchHistory];
          } else if (existingIndex > 0) {
            // Already exists → move to index 0
            const updated = [...state.searchHistory];
            updated.splice(existingIndex, 1); // remove from current position
            updated.unshift(newItem); // insert at top
            state.searchHistory = updated;
          }
        }
      });
  },
});

export const {
  uploadFileHandle,
  setFilterValues,
  resetFilterValues,
  updateFileProgress,
  clearUploadFiles,
  cancelUploadingFile,
  cancelUploadingAllFiles,
  updateFolderItemsInHome,
  renameFileInHome,
  uploadFileInGeneral,
  trashFilesAndFoldersGeneral,
  starUnstarItemsGeneral,
  lockUnlockItemsGeneral,
  updateFileAndFolderGeneral,
  removeFileAndFolderPemissionGeneral,
  createFileAndFolderPermission,
  moveFilesAndFoldersGeneral,
  recentItem,
  recentFolder,
  acceptShareRequest,
  uploadFolderHandle,
  setFolderUploadStatus,
  clearFolderUpload,
  updateStorageUsed,
  restoreFilesAndFoldersGeneral,
  restoreFilesOpenFile,
  permanentDeleteFilesOpenFile,
  emptyTrashOpenFile,
  updateSortOrderInGeneral,
  removeUploadFile,
  updateIsSharedGeneral,
} = generalSlice.actions;

export default generalSlice.reducer;
